import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Badge,
} from "@mui/material";
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ShoppingCart as OrderIcon,
  Inventory as InventoryIcon,
  TrendingDown as LowStockIcon,
  Error as CriticalIcon,
  Notifications as NotificationIcon,
  Business as VendorIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  LocalOffer as PriceIcon,
  Assessment as ReportIcon,
} from "@mui/icons-material";

const LowStockAlert = () => {
  // Sample low stock products data
  const [stockAlerts] = useState([
    {
      id: 1,
      productCode: "PRD001",
      productName: "Samsung Galaxy S23",
      category: "Electronics",
      location: "Main Warehouse",
      currentStock: 5,
      minStockLevel: 10,
      reorderPoint: 15,
      reorderQuantity: 50,
      unitPrice: 55000,
      preferredVendor: "ABC Suppliers Pvt Ltd",
      lastReorder: "2024-01-10",
      avgDailySales: 2,
      daysUntilStockout: 2,
      alertLevel: "Critical",
      status: "Active",
    },
    {
      id: 2,
      productCode: "PRD002",
      productName: "Apple iPhone 14",
      category: "Electronics",
      location: "Main Warehouse",
      currentStock: 8,
      minStockLevel: 15,
      reorderPoint: 20,
      reorderQuantity: 30,
      unitPrice: 75000,
      preferredVendor: "Global Imports LLC",
      lastReorder: "2024-01-05",
      avgDailySales: 1.5,
      daysUntilStockout: 5,
      alertLevel: "Warning",
      status: "Active",
    },
    {
      id: 3,
      productCode: "PRD003",
      productName: "Sony LED TV 55\"",
      category: "Electronics",
      location: "Andheri Store",
      currentStock: 2,
      minStockLevel: 5,
      reorderPoint: 8,
      reorderQuantity: 15,
      unitPrice: 45000,
      preferredVendor: "ABC Suppliers Pvt Ltd",
      lastReorder: "2024-01-15",
      avgDailySales: 0.5,
      daysUntilStockout: 4,
      alertLevel: "Critical",
      status: "Active",
    },
    {
      id: 4,
      productCode: "PRD004",
      productName: "HP Laptop i5",
      category: "Electronics",
      location: "Main Warehouse",
      currentStock: 12,
      minStockLevel: 20,
      reorderPoint: 25,
      reorderQuantity: 40,
      unitPrice: 42000,
      preferredVendor: "XYZ Wholesale",
      lastReorder: "2024-01-08",
      avgDailySales: 1,
      daysUntilStockout: 12,
      alertLevel: "Warning",
      status: "Active",
    },
    {
      id: 5,
      productCode: "PRD005",
      productName: "Nike Sports Shoes",
      category: "Clothing",
      location: "Bandra Store",
      currentStock: 15,
      minStockLevel: 30,
      reorderPoint: 40,
      reorderQuantity: 100,
      unitPrice: 4500,
      preferredVendor: "Premium Products Inc",
      lastReorder: "2024-01-12",
      avgDailySales: 3,
      daysUntilStockout: 5,
      alertLevel: "Warning",
      status: "Active",
    },
    {
      id: 6,
      productCode: "PRD010",
      productName: "Rice 1kg",
      category: "Groceries",
      location: "Main Warehouse",
      currentStock: 25,
      minStockLevel: 100,
      reorderPoint: 150,
      reorderQuantity: 500,
      unitPrice: 60,
      preferredVendor: "Local Traders",
      lastReorder: "2024-01-20",
      avgDailySales: 50,
      daysUntilStockout: 0.5,
      alertLevel: "Critical",
      status: "Active",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [alertFilter, setAlertFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");

  // Modal state
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);

  // Helper functions
  const getFilteredAlerts = () => {
    return stockAlerts.filter((alert) => {
      const matchesSearch = Object.values(alert).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesAlert = alertFilter === "all" || alert.alertLevel === alertFilter;
      const matchesCategory = categoryFilter === "all" || alert.category === categoryFilter;
      const matchesLocation = locationFilter === "all" || alert.location === locationFilter;
      return matchesSearch && matchesAlert && matchesCategory && matchesLocation;
    });
  };

  const getAlertStyle = (level) => {
    switch (level) {
      case 'Critical':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      case 'Warning':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      case 'Low':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const getAlertIcon = (level) => {
    switch (level) {
      case 'Critical':
        return CriticalIcon;
      case 'Warning':
        return WarningIcon;
      case 'Low':
        return InfoIcon;
      default:
        return InfoIcon;
    }
  };

  const getStockPercentage = (current, min) => {
    return min > 0 ? (current / min) * 100 : 100;
  };

  const getProgressColor = (percentage) => {
    if (percentage <= 30) return 'error';
    if (percentage <= 60) return 'warning';
    return 'success';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Handlers
  const handleView = (product) => {
    setViewingProduct(product);
    setOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingProduct(null);
  };

  const handleCreatePurchaseOrder = (product) => {
    setSuccessMessage(`Purchase order initiated for ${product.productName} (${product.reorderQuantity} units)`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredAlerts().length} alerts.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setAlertFilter("all");
    setCategoryFilter("all");
    setLocationFilter("all");
    setPage(0);
    setSuccessMessage("");
  };

  const filteredAlerts = getFilteredAlerts();

  // Calculate statistics
  const criticalAlerts = stockAlerts.filter(a => a.alertLevel === "Critical").length;
  const warningAlerts = stockAlerts.filter(a => a.alertLevel === "Warning").length;
  const totalValue = stockAlerts.reduce((sum, alert) => 
    sum + (alert.reorderQuantity * alert.unitPrice), 0
  );

  // Get unique values for filters
  const categories = [...new Set(stockAlerts.map(a => a.category))];
  const locations = [...new Set(stockAlerts.map(a => a.location))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge badgeContent={criticalAlerts} color="error">
            <LowStockIcon sx={{ color: 'warning.main', fontSize: 40 }} />
          </Badge>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Low Stock Alerts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor inventory levels and manage reorders • {filteredAlerts.length} products need attention
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ReportIcon />}
            sx={{ borderRadius: 2 }}
          >
            Export Report
          </Button>
          <Button
            variant="contained"
            startIcon={<OrderIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Bulk Reorder
          </Button>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #d32f2f' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Critical Alerts</Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {criticalAlerts}
                  </Typography>
                </Box>
                <CriticalIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #e65100' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Warning Alerts</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {warningAlerts}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Products</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {stockAlerts.length}
                  </Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Reorder Value</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {formatCurrency(totalValue)}
                  </Typography>
                </Box>
                <PriceIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Products..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Alert Level"
              value={alertFilter}
              onChange={(e) => setAlertFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Levels</option>
              <option value="Critical">Critical</option>
              <option value="Warning">Warning</option>
              <option value="Low">Low</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Locations</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Alert</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Product Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Stock Level</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Reorder Info</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Days to Stockout</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAlerts.length > 0 ? (
                filteredAlerts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((alert) => {
                    const stockPercentage = getStockPercentage(alert.currentStock, alert.minStockLevel);
                    const alertStyle = getAlertStyle(alert.alertLevel);
                    const AlertIconComponent = getAlertIcon(alert.alertLevel);
                    return (
                      <TableRow 
                        key={alert.id} 
                        sx={{ 
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          transition: 'background-color 0.2s',
                          backgroundColor: alert.alertLevel === 'Critical' ? 'rgba(255, 235, 238, 0.3)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Chip
                            icon={<AlertIconComponent />}
                            label={alert.alertLevel}
                            size="small"
                            sx={{
                              ...alertStyle,
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                            {alert.productCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {alert.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={alert.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption">
                              {alert.location}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography 
                                variant="caption" 
                                fontWeight="600" 
                                sx={{ 
                                  color: getProgressColor(stockPercentage) === 'error' ? 'error.main' : 
                                         getProgressColor(stockPercentage) === 'warning' ? 'warning.main' : 'success.main'
                                }}
                              >
                                {alert.currentStock} / {alert.minStockLevel}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {stockPercentage.toFixed(0)}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(stockPercentage, 100)} 
                              color={getProgressColor(stockPercentage)}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Reorder: {alert.reorderPoint}
                            </Typography>
                            <Typography variant="caption" display="block" fontWeight="600" color="primary.main">
                              Qty: {alert.reorderQuantity}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={alert.daysUntilStockout < 1 ? "< 1 day" : `${alert.daysUntilStockout} days`}
                            size="small"
                            color={alert.daysUntilStockout <= 2 ? "error" : alert.daysUntilStockout <= 5 ? "warning" : "default"}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VendorIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption">
                              {alert.preferredVendor}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleView(alert)}
                                sx={{
                                  color: 'info.main',
                                  '&:hover': { backgroundColor: 'info.light' },
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Create Purchase Order">
                              <IconButton
                                size="small"
                                onClick={() => handleCreatePurchaseOrder(alert)}
                                sx={{
                                  color: 'success.main',
                                  '&:hover': { backgroundColor: 'success.light' },
                                }}
                              >
                                <OrderIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Reorder Settings">
                              <IconButton
                                size="small"
                                sx={{
                                  color: 'warning.main',
                                  '&:hover': { backgroundColor: 'warning.light' },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <NotificationIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No low stock alerts found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All products are at healthy stock levels
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAlerts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* View Product Details Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            Stock Alert Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingProduct && (
            <Grid container spacing={3}>
              {/* Alert Banner */}
              <Grid item xs={12}>
                {(() => {
                  const AlertIconComponent = getAlertIcon(viewingProduct.alertLevel);
                  return (
                    <Alert 
                      severity={viewingProduct.alertLevel === 'Critical' ? 'error' : 'warning'}
                      icon={<AlertIconComponent />}
                      sx={{ borderRadius: 2 }}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        {viewingProduct.alertLevel} Alert: Stock level is {viewingProduct.alertLevel === 'Critical' ? 'critically' : 'significantly'} low
                      </Typography>
                      <Typography variant="body2">
                        Estimated {viewingProduct.daysUntilStockout} days until stockout based on average sales
                      </Typography>
                    </Alert>
                  );
                })()}
              </Grid>

              {/* Product Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Product Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Product Code</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingProduct.productCode}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Product Name</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingProduct.productName}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingProduct.category}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LocationIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body1" fontWeight="bold">
                      {viewingProduct.location}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Unit Price</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }} color="success.main">
                    {formatCurrency(viewingProduct.unitPrice)}
                  </Typography>
                </Paper>
              </Grid>

              {/* Stock Levels */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Stock Levels
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }} color="error.main">
                    {viewingProduct.currentStock}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Minimum Stock Level</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }} color="warning.main">
                    {viewingProduct.minStockLevel}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(33, 150, 243, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Reorder Point</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }} color="primary.main">
                    {viewingProduct.reorderPoint}
                  </Typography>
                </Paper>
              </Grid>

              {/* Reorder Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Reorder Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Recommended Reorder Quantity</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }} color="success.main">
                    {viewingProduct.reorderQuantity} units
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Estimated Reorder Cost</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="success.main">
                    {formatCurrency(viewingProduct.reorderQuantity * viewingProduct.unitPrice)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Preferred Vendor</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <VendorIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body1" fontWeight="bold">
                      {viewingProduct.preferredVendor}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Last Reorder Date</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingProduct.lastReorder}
                  </Typography>
                </Paper>
              </Grid>

              {/* Sales Analytics */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Sales Analytics
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(156, 39, 176, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Average Daily Sales</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }} color="secondary.main">
                    {viewingProduct.avgDailySales} units/day
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Days Until Stockout</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }} color="error.main">
                    {viewingProduct.daysUntilStockout} {viewingProduct.daysUntilStockout === 1 ? 'day' : 'days'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
          {viewingProduct && (
            <Button
              variant="contained"
              onClick={() => {
                handleCreatePurchaseOrder(viewingProduct);
                handleCloseViewModal();
              }}
              startIcon={<OrderIcon />}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Create Purchase Order
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LowStockAlert;

